// New Claim Flow - State Management Hook
// Manages the entire claim creation state machine

import { useReducer, useCallback, useEffect, useRef } from 'react';
import type {
  ClaimState,
  ClaimAction,
  ClaimPhase,
  FlightSegment,
  EditableSegment,
  Passenger,
  IncidentType,
  Expense,
  UploadedDocument,
  ExtractedBookingData,
} from './types';
import {
  getNextPhase,
  phaseConfig,
  storageKeys,
  getHardRequirements,
  type DocumentRequirement,
} from './constants';
import { claimReducer, initialState } from './reducers';

// ============================================================================
// Hook Interface
// ============================================================================

export interface UseClaimStateReturn {
  state: ClaimState;
  dispatch: React.Dispatch<ClaimAction>;

  // Phase navigation
  goToPhase: (phase: ClaimPhase) => void;
  goBack: () => void;
  goNext: () => void;
  canGoBack: boolean;
  canSkip: boolean;

  // Booking actions
  setExtractedData: (data: ExtractedBookingData) => void;
  updateSegment: (id: string, field: keyof FlightSegment, value: string) => void;
  deleteSegment: (id: string) => void;
  addSegment: () => void;
  updatePassenger: (id: string, field: 'first_name' | 'last_name', value: string) => void;
  deletePassenger: (id: string) => void;
  addPassenger: () => void;
  setPnr: (pnr: string) => void;

  // Incident actions
  setIncidentType: (type: IncidentType) => void;
  setAffectedSegment: (segmentId: string | null) => void;
  setIncidentDescription: (desc: string) => void;

  // Evidence actions
  addDocument: (doc: UploadedDocument) => void;
  updateDocument: (id: string, updates: Partial<UploadedDocument>) => void;
  removeDocument: (id: string) => void;
  addExpense: (expense: Expense) => void;
  removeExpense: (id: string) => void;

  // Narrative
  setNarrative: (text: string) => void;

  // Persistence
  setCaseId: (caseId: string, token: string) => void;
  setError: (error: string | null) => void;
  markSaved: () => void;

  // Reset
  reset: () => void;

  // Computed
  visibleSegments: EditableSegment[];
  totalExpenses: number;
  hasRequiredData: boolean;
}

export function useClaimState(): UseClaimStateReturn {
  const [state, dispatch] = useReducer(claimReducer, initialState);
  const autoTransitionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle auto-transitions for intro phases
  useEffect(() => {
    const config = phaseConfig[state.phase];
    if (config.autoTransition) {
      autoTransitionTimeoutRef.current = setTimeout(() => {
        dispatch({ type: 'GO_TO_PHASE', phase: config.autoTransition!.to });
      }, config.autoTransition.delay);
    }

    return () => {
      if (autoTransitionTimeoutRef.current) {
        clearTimeout(autoTransitionTimeoutRef.current);
      }
    };
  }, [state.phase]);

  // Phase navigation
  const goToPhase = useCallback((phase: ClaimPhase) => {
    dispatch({ type: 'GO_TO_PHASE', phase });
  }, []);

  const goBack = useCallback(() => {
    dispatch({ type: 'GO_BACK' });
  }, []);

  const goNext = useCallback(() => {
    const next = getNextPhase(state.phase);
    if (next) {
      dispatch({ type: 'GO_TO_PHASE', phase: next });
    }
  }, [state.phase]);

  const canGoBack = phaseConfig[state.phase]?.canGoBack ?? false;
  const canSkip = phaseConfig[state.phase]?.canSkip ?? false;

  // Booking actions
  const setExtractedData = useCallback((data: ExtractedBookingData) => {
    dispatch({ type: 'SET_EXTRACTED_DATA', data });
  }, []);

  const updateSegment = useCallback(
    (id: string, field: keyof FlightSegment, value: string) => {
      dispatch({ type: 'UPDATE_SEGMENT', id, field, value });
    },
    []
  );

  const deleteSegment = useCallback((id: string) => {
    dispatch({ type: 'DELETE_SEGMENT', id });
  }, []);

  const addSegment = useCallback(() => {
    dispatch({ type: 'ADD_SEGMENT' });
  }, []);

  const updatePassenger = useCallback(
    (id: string, field: 'first_name' | 'last_name', value: string) => {
      dispatch({ type: 'UPDATE_PASSENGER', id, field, value });
    },
    []
  );

  const deletePassenger = useCallback((id: string) => {
    dispatch({ type: 'DELETE_PASSENGER', id });
  }, []);

  const addPassenger = useCallback(() => {
    dispatch({ type: 'ADD_PASSENGER' });
  }, []);

  const setPnr = useCallback((pnr: string) => {
    dispatch({ type: 'SET_PNR', pnr });
  }, []);

  // Incident actions
  const setIncidentType = useCallback((incidentType: IncidentType) => {
    dispatch({ type: 'SET_INCIDENT_TYPE', incidentType });
  }, []);

  const setAffectedSegment = useCallback((segmentId: string | null) => {
    dispatch({ type: 'SET_AFFECTED_SEGMENT', segmentId });
  }, []);

  const setIncidentDescription = useCallback((description: string) => {
    dispatch({ type: 'SET_INCIDENT_DESCRIPTION', description });
  }, []);

  // Evidence actions
  const addDocument = useCallback((document: UploadedDocument) => {
    dispatch({ type: 'ADD_DOCUMENT', document });
  }, []);

  const updateDocument = useCallback(
    (id: string, updates: Partial<UploadedDocument>) => {
      dispatch({ type: 'UPDATE_DOCUMENT', id, updates });
    },
    []
  );

  const removeDocument = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_DOCUMENT', id });
  }, []);

  const addExpense = useCallback((expense: Expense) => {
    dispatch({ type: 'ADD_EXPENSE', expense });
  }, []);

  const removeExpense = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_EXPENSE', id });
  }, []);

  // Narrative
  const setNarrative = useCallback((narrative: string) => {
    dispatch({ type: 'SET_NARRATIVE', narrative });
  }, []);

  // Persistence
  const setCaseId = useCallback((caseId: string, caseNumber: string) => {
    dispatch({ type: 'SET_CASE_ID', caseId, caseNumber });
  }, []);

  const setError = useCallback((error: string | null) => {
    dispatch({ type: 'SET_ERROR', error });
  }, []);

  const markSaved = useCallback(() => {
    dispatch({ type: 'SET_LAST_SAVED', timestamp: new Date().toISOString() });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  // Computed values
  const visibleSegments = state.editableSegments.filter(
    (seg) => !seg._isDeleted
  );

  const totalExpenses = state.expenses.reduce(
    (sum, e) => sum + e.amount_cents,
    0
  );

  const hasRequiredData =
    visibleSegments.length > 0 &&
    state.passengers.length > 0 &&
    state.incidentType !== null;

  return {
    state,
    dispatch,

    goToPhase,
    goBack,
    goNext,
    canGoBack,
    canSkip,

    setExtractedData,
    updateSegment,
    deleteSegment,
    addSegment,
    updatePassenger,
    deletePassenger,
    addPassenger,
    setPnr,

    setIncidentType,
    setAffectedSegment,
    setIncidentDescription,

    addDocument,
    updateDocument,
    removeDocument,
    addExpense,
    removeExpense,

    setNarrative,

    setCaseId,
    setError,
    markSaved,
    reset,

    visibleSegments,
    totalExpenses,
    hasRequiredData,
  };
}

// ============================================================================
// localStorage Draft Persistence
// ============================================================================

export function saveDraftToStorage(state: ClaimState): void {
  try {
    const draft = {
      savedAt: Date.now(),
      state: {
        caseId: state.caseId,
        caseNumber: state.caseNumber,
        phase: state.phase,
        editableSegments: state.editableSegments,
        passengers: state.passengers,
        pnr: state.pnr,
        bookingDate: state.bookingDate,
        incidentType: state.incidentType,
        affectedSegmentId: state.affectedSegmentId,
        incidentDescription: state.incidentDescription,
        expenses: state.expenses,
        narrative: state.narrative,
        inboundEmailAddress: state.inboundEmailAddress,
        // Pending token
        pendingToken: state.pendingToken,
        tokenExpiresAt: state.tokenExpiresAt,
      },
    };
    localStorage.setItem(storageKeys.draft, JSON.stringify(draft));
  } catch {
    // Ignore storage errors
  }
}

export function loadDraftFromStorage(): Partial<ClaimState> | null {
  try {
    const stored = localStorage.getItem(storageKeys.draft);
    if (!stored) return null;

    const draft = JSON.parse(stored);
    const age = Date.now() - draft.savedAt;

    // Expire after 24 hours
    if (age > storageKeys.draftExpiry) {
      localStorage.removeItem(storageKeys.draft);
      return null;
    }

    return draft.state;
  } catch {
    return null;
  }
}

export function clearDraftFromStorage(): void {
  try {
    localStorage.removeItem(storageKeys.draft);
  } catch {
    // Ignore
  }
}

// ============================================================================
// Database Draft Persistence
// ============================================================================

import api from '@/utils/api';

/**
 * Map claim phase to intake step name for backend compatibility
 */
function mapPhaseToIntakeStep(phase: ClaimPhase): string {
  const mapping: Record<ClaimPhase, string> = {
    welcome: 'original_booking',
    extracting: 'original_booking',
    awaiting_email: 'original_booking',
    booking_review: 'original_booking',
    incident_intro: 'what_happened',
    incident_select: 'what_happened',
    incident_details: 'what_happened',
    evidence_intro: 'documentation',
    documentation: 'documentation',
    expenses: 'expenses',
    narrative: 'narrative',
    review: 'review',
    submitting: 'review',
    complete: 'completed',
    error: 'original_booking',
  };
  return mapping[phase] || 'original_booking';
}

/**
 * Save draft to database via intake/progress endpoint
 * Only called when caseId exists (case has been created)
 */
export async function saveDraftToDatabase(
  caseId: string,
  state: ClaimState
): Promise<void> {
  if (!caseId) return;

  const stepData = {
    new_flow: true, // Mark this as new flow for differentiation from legacy wizard
    phase: state.phase,
    editableSegments: state.editableSegments,
    passengers: state.passengers,
    pnr: state.pnr,
    bookingDate: state.bookingDate,
    incidentType: state.incidentType,
    affectedSegmentId: state.affectedSegmentId,
    incidentDescription: state.incidentDescription,
    expenses: state.expenses,
    narrative: state.narrative,
    inboundEmailAddress: state.inboundEmailAddress,
  };

  await api.put(`/api/cases/${caseId}/intake/progress/`, {
    current_step: mapPhaseToIntakeStep(state.phase),
    step_data: stepData,
  });
}

/**
 * Load draft from database for resuming a claim
 */
export async function loadDraftFromDatabase(
  caseId: string
): Promise<Partial<ClaimState> | null> {
  try {
    const response = await api.get(`/api/cases/${caseId}/`);
    const caseData = response.data.case || response.data;

    // Only restore if it's a new flow case
    if (!caseData.intake_data?.new_flow) {
      return null;
    }

    const savedData = caseData.intake_data;
    return {
      caseId: String(caseData.id),
      caseNumber: caseData.case_number,
      phase: savedData.phase || 'booking_review',
      editableSegments: savedData.editableSegments || [],
      passengers: savedData.passengers || [],
      pnr: savedData.pnr || null,
      bookingDate: savedData.bookingDate || null,
      incidentType: savedData.incidentType || null,
      affectedSegmentId: savedData.affectedSegmentId || null,
      incidentDescription: savedData.incidentDescription || '',
      expenses: savedData.expenses || [],
      narrative: savedData.narrative || '',
      inboundEmailAddress: savedData.inboundEmailAddress || null,
    };
  } catch (err) {
    console.error('Failed to load draft from database:', err);
    return null;
  }
}

/**
 * Create a new case in the database
 * Returns { caseId, caseNumber }
 */
export async function createCaseInDatabase(): Promise<{
  caseId: string;
  caseNumber: string;
}> {
  const response = await api.post('/api/cases/', {});

  if (!response.data.case?.id) {
    throw new Error('Failed to create case: No case ID returned');
  }

  return {
    caseId: String(response.data.case.id),
    caseNumber: response.data.case.case_number,
  };
}

// ============================================================================
// Document Requirement Helpers
// ============================================================================

/**
 * Check if a document satisfies a requirement
 * Either by explicit user assignment (fulfillsRequirement) OR by AI category matching
 */
function documentSatisfiesRequirement(
  doc: UploadedDocument,
  requirement: DocumentRequirement
): boolean {
  // Skip uploading or errored documents
  if (doc.isUploading || doc.error) return false;

  // Priority 1: Explicit user assignment via fulfillsRequirement
  if (doc.fulfillsRequirement === requirement.id) return true;

  // Priority 2: AI category match
  if (doc.category && requirement.satisfiedByKinds.includes(doc.category)) return true;

  return false;
}

/**
 * Check if all hard document requirements are met for the current incident type
 */
export function areHardRequirementsMet(
  incidentType: IncidentType | null,
  documents: UploadedDocument[]
): boolean {
  const hardReqs = getHardRequirements(incidentType);
  if (hardReqs.length === 0) return true;

  return hardReqs.every((req) =>
    documents.some((doc) => documentSatisfiesRequirement(doc, req))
  );
}

/**
 * Get the status of a specific document requirement
 */
export function getRequirementStatus(
  requirement: DocumentRequirement,
  documents: UploadedDocument[]
): 'satisfied' | 'pending' | 'missing' {
  // Check if any document satisfies this requirement
  if (documents.some((doc) => documentSatisfiesRequirement(doc, requirement))) {
    return 'satisfied';
  }

  // Check if there's a pending upload for this requirement
  if (documents.some((d) => d.isUploading && d.fulfillsRequirement === requirement.id)) {
    return 'pending';
  }

  return 'missing';
}

/**
 * Get documents that satisfy a specific requirement
 */
export function getDocumentsForRequirement(
  requirement: DocumentRequirement,
  documents: UploadedDocument[]
): UploadedDocument[] {
  return documents.filter((doc) => documentSatisfiesRequirement(doc, requirement));
}
